import { useEffect, useState } from "react";
import { toast } from "sonner";
import { IdCard } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { http, formatApiError } from "../lib/api";
import { DIPLOMAS, ID_DOC_TYPES, CONTRACT_TYPES, contractRequiresEnd } from "../lib/diplomas";
import { useActivities } from "../lib/activities";
import { NewActivityButton } from "./NewActivity";
import { DateInput } from "./DateInput";
import { AvatarEditor } from "./UserAvatar";

const inp = "w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white";
const F = ({ label, children, wide }) => <label className={`block text-[11px] text-slate-500 ${wide ? "col-span-2" : ""}`}>{label}{children}</label>;
const Sec = ({ title, children }) => (
  <div className="border border-slate-200 rounded-md p-3">
    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">{title}</div>
    <div className="grid grid-cols-2 gap-3">{children}</div>
  </div>
);
const SELF = ["first_name", "last_name", "phone", "birth_date", "birth_city", "birth_country", "nationality", "address", "postal_code", "city",
  "social_security_number", "id_doc_type", "id_doc_number", "id_doc_expires_on", "carte_pro_number"];
const ADMIN = ["contract_type", "contract_start_date", "contract_start_time", "contract_end_date", "qualification", "coefficient", "level", "echelon", "contract_hours", "matricule"];

function Diplomas({ value, onChange }) {
  const set = (code, k, x) => onChange({ ...value, [code]: { ...(value[code] || {}), [k]: x } });
  return (
    <div className="border border-slate-200 rounded-md p-3" data-testid="staff-diplomas">
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Diplômes et habilitations</div>
      <div className="space-y-2">
        {DIPLOMAS.map((d) => (
          <div key={d.code} className="grid grid-cols-[110px_1fr_150px] gap-2 items-center">
            <span className="text-sm font-semibold">{d.label}</span>
            <input data-testid={`staff-dip-${d.code}-number`} className={inp} placeholder="N° du diplôme" value={value[d.code]?.number || ""} onChange={(e) => set(d.code, "number", e.target.value)} />
            <DateInput data-testid={`staff-dip-${d.code}-exp`} className={inp} value={value[d.code]?.expires_on || ""} onChange={(e) => set(d.code, "expires_on", e.target.value)} />
          </div>
        ))}
      </div>
      <p className="text-[10px] text-slate-500 mt-2">Date de renouvellement facultative : une alerte apparaît 30 jours avant l'échéance.</p>
    </div>
  );
}

function Contract({ v, set }) {
  const acts = useActivities();
  return (
    <Sec title="Contrat (réservé à l'administrateur)">
      <F label="Type de contrat"><select data-testid="staff-contract-type" className={inp} value={v.contract_type || ""} onChange={(e) => set("contract_type", e.target.value)}><option value="">—</option>{CONTRACT_TYPES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}</select></F>
      <F label="Qualification / emploi">
        <select data-testid="staff-qualification" className={inp} value={v.qualification || ""} onChange={(e) => set("qualification", e.target.value)}><option value="">—</option>{acts.map((a) => <option key={a.code} value={a.code}>{a.label}</option>)}</select>
        <NewActivityButton testid="staff-qualification-new" className="mt-1" onCreated={(c) => set("qualification", c)} />
      </F>
      <F label="Date d'entrée (registre)"><DateInput data-testid="staff-start-date" className={inp} value={v.contract_start_date || ""} onChange={(e) => set("contract_start_date", e.target.value)} /></F>
      {contractRequiresEnd(v.contract_type) ? <F label="Date de fin"><DateInput data-testid="staff-end-date" className={inp} value={v.contract_end_date || ""} onChange={(e) => set("contract_end_date", e.target.value)} /></F>
        : <F label="Heure d'embauche"><input data-testid="staff-start-time" type="time" className={inp} value={v.contract_start_time || ""} onChange={(e) => set("contract_start_time", e.target.value)} /></F>}
      <F label="Coefficient"><input data-testid="staff-coefficient" className={inp} value={v.coefficient || ""} onChange={(e) => set("coefficient", e.target.value)} /></F>
      <F label="Heures du contrat / mois"><input data-testid="staff-hours" type="number" step="0.01" min="0" className={inp} value={v.contract_hours ?? ""} onChange={(e) => set("contract_hours", e.target.value)} /></F>
      <F label="Niveau"><input data-testid="staff-level" className={inp} value={v.level || ""} onChange={(e) => set("level", e.target.value)} /></F>
      <F label="Échelon"><input data-testid="staff-echelon" className={inp} value={v.echelon || ""} onChange={(e) => set("echelon", e.target.value)} /></F>
    </Sec>
  );
}

function Identity({ v, set }) {
  const t = (k, label, extra = {}) => <F label={label}><input data-testid={`staff-${k.replace(/_/g, "-")}`} className={inp} value={v[k] || ""} onChange={(e) => set(k, e.target.value)} {...extra} /></F>;
  return (
    <>
      <Sec title="Identité et coordonnées">
        {t("last_name", "Nom *")}{t("first_name", "Prénom *")}
        <F label="Date de naissance"><DateInput data-testid="staff-birth-date" className={inp} value={v.birth_date || ""} onChange={(e) => set("birth_date", e.target.value)} /></F>
        {t("birth_city", "Ville de naissance")}{t("birth_country", "Pays de naissance")}{t("nationality", "Nationalité")}
        {t("phone", "Téléphone")}{t("social_security_number", "N° de sécurité sociale")}
        <F label="Adresse" wide><input data-testid="staff-address" className={inp} value={v.address || ""} onChange={(e) => set("address", e.target.value)} /></F>
        {t("postal_code", "Code postal")}{t("city", "Ville")}
      </Sec>
      <Sec title="Pièce d'identité et carte professionnelle">
        <F label="Type de pièce"><select data-testid="staff-id-doc-type" className={inp} value={v.id_doc_type || ""} onChange={(e) => set("id_doc_type", e.target.value)}><option value="">—</option>{ID_DOC_TYPES.map((x) => <option key={x.code} value={x.code}>{x.label}</option>)}</select></F>
        {t("id_doc_number", "N° de la pièce")}
        <F label="Expire le"><DateInput data-testid="staff-id-doc-exp" className={inp} value={v.id_doc_expires_on || ""} onChange={(e) => set("id_doc_expires_on", e.target.value)} /></F>
        {t("carte_pro_number", "N° carte professionnelle CNAPS")}
      </Sec>
    </>
  );
}

export const StaffProfileDialog = ({ uid, onClose, onSaved }) => {
  const [d, setD] = useState(null);
  const [v, setV] = useState({});
  const [dip, setDip] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    http.get(`/staff-profiles/${uid}`).then((r) => {
      const p = r.data.profile || {};
      const [fn, ...ln] = (r.data.user.name || "").split(" ");
      setD(r.data); setV(r.data.profile ? p : { first_name: fn, last_name: ln.join(" ") }); setDip(p.diplomas || {});
    }).catch((e) => { toast.error(formatApiError(e)); onClose(); });
  }, [uid]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k, x) => setV((o) => ({ ...o, [k]: x }));
  const save = async () => {
    const keys = d.can_admin ? [...SELF, ...ADMIN] : SELF;
    const body = Object.fromEntries(keys.map((k) => [k, v[k] === "" || v[k] == null ? null : v[k]]));
    if (body.contract_hours != null) body.contract_hours = Number(body.contract_hours);
    if (d.can_admin && !contractRequiresEnd(v.contract_type)) body.contract_end_date = null;
    body.diplomas = dip;
    setBusy(true);
    try { await http.put(`/staff-profiles/${uid}`, body); toast.success("Fiche salarié enregistrée"); onSaved?.(); onClose(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="staff-profile-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><IdCard className="h-6 w-6 text-sky-600" /> Fiche salarié{d ? ` — ${d.user.name}` : ""}</DialogTitle></DialogHeader>
        {!d ? <div className="py-10 text-center text-sm text-slate-500">Chargement…</div> : (
          <div className="space-y-3">
            <AvatarEditor uid={uid} u={d.user} onChanged={() => http.get(`/staff-profiles/${uid}`).then((r) => setD((o) => ({ ...o, user: r.data.user })))} />
            <p className="text-xs text-slate-500">Ces informations alimentent le registre du personnel. Avec un diplôme valide, la personne peut aussi être placée sur des vacations du planning, sans espace agent.</p>
            <Identity v={v} set={set} />
            <Diplomas value={dip} onChange={setDip} />
            {d.can_admin && <Contract v={v} set={set} />}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-md border border-slate-300" data-testid="staff-cancel">Annuler</button>
          <button type="button" onClick={save} disabled={!d || busy || !(v.first_name || "").trim() || !(v.last_name || "").trim()} className="px-4 py-2 text-sm rounded-md bg-sky-600 text-white font-semibold disabled:opacity-40" data-testid="staff-save">{busy ? "Enregistrement…" : "Enregistrer"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
